import * as React from "react"

import { UserBlobatar } from "@/components/chat/blobatar"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import type { User } from "@/chat/protocol"
import { useChat } from "@/chat/store"

/** Debounce for the login-prefix search; a keystroke is not a query. */
const SEARCH_DELAY_MS = 250

/**
 * Finds people by login prefix. Used wherever a person has to be chosen: a new
 * direct room, the members of a new group, an invitation into one.
 */
export function UserPicker({
  placeholder,
  exclude,
  busyId = null,
  onPick,
  autoFocus,
}: {
  placeholder: string
  /** Ids never offered — the caller, and whoever is already in the room. */
  exclude?: ReadonlySet<string>
  /** A pick in flight; the row shows a spinner and the list is disabled. */
  busyId?: string | null
  onPick: (user: User) => void
  autoFocus?: boolean
}) {
  const { searchUsers } = useChat()
  const [query, setQuery] = React.useState("")
  /** The last answer, tagged with the prefix it was for so stale ones don't show. */
  const [found, setFound] = React.useState<{
    prefix: string
    users: User[]
  } | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const prefix = query.trim()
  const results = found?.prefix === prefix ? found.users : null
  const searching = prefix !== "" && results === null && error === null

  React.useEffect(() => {
    if (!prefix) return

    let cancelled = false

    const timer = setTimeout(() => {
      searchUsers(prefix)
        .then((users) => {
          if (cancelled) return
          setFound({ prefix, users })
          setError(null)
        })
        .catch((caught: unknown) => {
          if (cancelled) return
          setError(
            caught instanceof Error ? caught.message : "Поиск не удался."
          )
        })
    }, SEARCH_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [prefix, searchUsers])

  const offered = (results ?? []).filter((u) => !exclude?.has(u.id))

  return (
    <div className="flex flex-col gap-2">
      <Input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setError(null)
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        autoCapitalize="none"
        spellCheck={false}
        maxLength={32}
      />

      {error && <p className="text-xs text-destructive">{error}</p>}

      {prefix && !error && (
        <ul className="flex max-h-56 flex-col gap-0.5 overflow-y-auto">
          {offered.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(person)
                  setQuery("")
                }}
                disabled={busyId !== null}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-60"
              >
                <UserBlobatar name={person.name} className="size-7" />
                <span className="truncate">{person.name}</span>
                {busyId === person.id && (
                  <Spinner className="ml-auto size-3.5" />
                )}
              </button>
            </li>
          ))}

          {offered.length === 0 && (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">
              {searching ? "Ищем…" : "Никого не нашлось."}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
